import * as Yup from 'yup';
import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useFormik, Form, FormikProvider } from 'formik';
import { Icon } from '@iconify/react';
import eyeFill from '@iconify/icons-eva/eye-fill';
import eyeOffFill from '@iconify/icons-eva/eye-off-fill';
import {
    Card, Stack, Container, Typography, TextField,
    IconButton, InputAdornment, Alert, Box,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { styled } from '@mui/material/styles';
import Page from 'src/components/Page';
import syndicationApi from 'src/utils/syndicationApi';

const RootStyle = styled(Page)(({ theme }) => ({
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
    padding: theme.spacing(4, 0),
}));

const FormCard = styled(Card)(({ theme }) => ({
    maxWidth: 480,
    width: '100%',
    padding: theme.spacing(5),
    borderRadius: 16,
    boxShadow: '0 8px 40px rgba(0,0,0,0.12)',
}));

export default function FounderSetPassword() {
    const { token } = useParams();
    const navigate = useNavigate();
    const [showPassword, setShowPassword] = useState(false);
    const [apiError, setApiError] = useState('');
    const [success, setSuccess] = useState(false);

    const Schema = Yup.object().shape({
        new_password: Yup.string()
            .min(8, 'At least 8 characters')
            .matches(/[A-Z]/, 'Must contain an uppercase letter')
            .matches(/[a-z]/, 'Must contain a lowercase letter')
            .matches(/[0-9]/, 'Must contain a digit')
            .matches(/[!@#$%^&*(),.?":{}|<>]/, 'Must contain a special character')
            .required('Password is required'),
        confirm_password: Yup.string()
            .oneOf([Yup.ref('new_password')], 'Passwords must match')
            .required('Confirm your password'),
    });

    const formik = useFormik({
        initialValues: { new_password: '', confirm_password: '' },
        validationSchema: Schema,
        onSubmit: async (values, { setSubmitting }) => {
            setApiError('');
            try {
                await syndicationApi.post('founder/set-password/', {
                    token,
                    new_password: values.new_password,
                });
                setSuccess(true);
            } catch (err) {
                const data = err.response?.data;
                setApiError(data?.message || 'Failed to set password.');
            } finally {
                setSubmitting(false);
            }
        },
    });

    return (
        <RootStyle title="Set Password | Gateway X">
            <Container maxWidth="sm" sx={{ display: 'flex', justifyContent: 'center' }}>
                <FormCard>
                    <Box sx={{ textAlign: 'center', mb: 4 }}>
                        <Typography variant="h4" sx={{ fontWeight: 700 }}>Set Your Password</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Gateway X Syndication — Founder Portal
                        </Typography>
                    </Box>

                    {apiError && <Alert severity="error" sx={{ mb: 2 }}>{apiError}</Alert>}

                    {success ? (
                        <Alert severity="success">
                            Password set successfully! Check your email for 2FA setup instructions, then{' '}
                            <Box component="span" sx={{ cursor: 'pointer', textDecoration: 'underline', fontWeight: 600 }}
                                onClick={() => navigate('/founder/login')}>
                                log in here
                            </Box>.
                        </Alert>
                    ) : (
                        <FormikProvider value={formik}>
                            <Form autoComplete="off" noValidate onSubmit={formik.handleSubmit}>
                                <Stack spacing={3}>
                                    <TextField
                                        fullWidth label="New Password"
                                        type={showPassword ? 'text' : 'password'}
                                        {...formik.getFieldProps('new_password')}
                                        error={Boolean(formik.touched.new_password && formik.errors.new_password)}
                                        helperText={formik.touched.new_password && formik.errors.new_password}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                                                        <Icon icon={showPassword ? eyeFill : eyeOffFill} />
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                    <TextField
                                        fullWidth label="Confirm Password"
                                        type={showPassword ? 'text' : 'password'}
                                        {...formik.getFieldProps('confirm_password')}
                                        error={Boolean(formik.touched.confirm_password && formik.errors.confirm_password)}
                                        helperText={formik.touched.confirm_password && formik.errors.confirm_password}
                                    />
                                    <LoadingButton
                                        fullWidth size="large" type="submit" variant="contained"
                                        loading={formik.isSubmitting}
                                        sx={{ bgcolor: '#1a237e', '&:hover': { bgcolor: '#0d1642' } }}
                                    >
                                        Set Password
                                    </LoadingButton>
                                </Stack>
                            </Form>
                        </FormikProvider>
                    )}
                </FormCard>
            </Container>
        </RootStyle>
    );
}
